import { BaseUITools } from "@/uiControls/base/baseUITools";
import { VisionaryScene } from '@/visionaryControllers/visionaryScene';
import { ViewHelperController } from '@/visionaryControllers/viewhelperController';

import * as THREE from 'three';
// 渲染器类型：支持 WebGLRenderer 和 WebGPURenderer
type RendererType = THREE.WebGLRenderer | any;

export enum ViewDirection {
    POSITIVE_X = '+x',
    NEGATIVE_X = '-x',
    POSITIVE_Y = '+y',
    NEGATIVE_Y = '-y',
    POSITIVE_Z = '+z',
    NEGATIVE_Z = '-z'
}

export interface AxisColorConfig {
    /** Axis color (also used for positive sphere and outline) */
    color: number;
    /** Negative sphere color (default: color * 0.8) */
    negativeSphere?: number;
}

export interface ViewHelperGizmoConfig {
    /** Axis length (default: 1.0) */
    size?: number;
    /** Color configuration for each axis */
    axisColors?: {
        x?: AxisColorConfig;
        y?: AxisColorConfig;
        z?: AxisColorConfig;
    };
}

interface LabelInfo {
    sprite: THREE.Sprite;
    sphere: THREE.Mesh;
    isPositive: boolean;
    blackTexture?: THREE.CanvasTexture;
    whiteTexture: THREE.CanvasTexture;
}

interface CircleOutlineInfo {
    outlineSphere: THREE.Mesh;
    targetSphere: THREE.Mesh;
}

/**
 * 内部 ViewHelperGizmo 类实现
 * 基于 PaleEngineWeb 的 ViewHelperGizmo，适配标准 Three.js
 */
class ViewHelperGizmo {
    private scene!: THREE.Scene;
    private camera!: THREE.OrthographicCamera;
    private gizmoGroup!: THREE.Group;
    private sphereGroup!: THREE.Group;
    private spheres: Map<THREE.Mesh, ViewDirection> = new Map();
    private labels: LabelInfo[] = [];
    private circleOutlines: CircleOutlineInfo[] = [];
    private raycaster: THREE.Raycaster;
    private axisLength: number;
    private sphereRadius: number = 0.15;
    private labelOffset: number = 0.05;
    private axisColors?: ViewHelperGizmoConfig['axisColors'];
    private hoveredSphere: THREE.Mesh | null = null;
    
    constructor(config: ViewHelperGizmoConfig = {}) {
        this.axisLength = config.size || 1.0;
        this.axisColors = config.axisColors;
        this.raycaster = new THREE.Raycaster();
        
        this.scene = new THREE.Scene();
        // 设置场景背景为透明（null 表示无背景）
        this.scene.background = null;
        this.createCamera();
        this.createGizmo();
    }
    
    private createCamera(): void {
        this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 120);
        this.camera.position.set(0, 0, 100);
        this.camera.lookAt(0, 0, 0);
    }
    
    private createGizmo(): void {
        this.gizmoGroup = new THREE.Group();
        this.sphereGroup = new THREE.Group();
        
        const defaultColors = {
            x: { color: 0xff0000 },
            y: { color: 0x00ff00 },
            z: { color: 0x0000ff }
        };
        
        const xColor = this.axisColors?.x ?? defaultColors.x;
        const yColor = this.axisColors?.y ?? defaultColors.y;
        const zColor = this.axisColors?.z ?? defaultColors.z;
        
        this.createAxis('x', xColor, new THREE.Vector3(1, 0, 0));
        this.createAxis('y', yColor, new THREE.Vector3(0, 1, 0));
        this.createAxis('z', zColor, new THREE.Vector3(0, 0, 1));
        
        this.gizmoGroup.add(this.sphereGroup);
        this.scene.add(this.gizmoGroup);
    }
    
    private createAxis(axisName: string, colorConfig: AxisColorConfig, direction: THREE.Vector3): void {
        const arrowLength = this.axisLength;
        const axisColor = colorConfig.color;
        const positiveSphereColor = axisColor;
        const negativeSphereColor = colorConfig.negativeSphere ?? (axisColor * 0.8);
        
        const arrowHelper = new THREE.ArrowHelper(
            direction,
            new THREE.Vector3(0, 0, 0),
            arrowLength,
            axisColor,
            0.05, // head length
            0.05  // head width
        );
        
        this.gizmoGroup.add(arrowHelper);
        
        const sphereGeometry = new THREE.SphereGeometry(this.sphereRadius, 16, 16);
        
        const positiveSphere = new THREE.Mesh(sphereGeometry, new THREE.MeshBasicMaterial({ 
            color: positiveSphereColor
        }));
        positiveSphere.position.copy(direction.clone().multiplyScalar(arrowLength));
        const negativeSphere = new THREE.Mesh(sphereGeometry, new THREE.MeshBasicMaterial({ 
            color: negativeSphereColor
        }));
        negativeSphere.position.copy(direction.clone().multiplyScalar(-arrowLength));
        const scalar = 0.8;
        negativeSphere.scale.set(scalar, scalar, scalar);
        
        const outlineSphereGeometry = new THREE.SphereGeometry(this.sphereRadius, 16, 16);
        const outlineSphere = new THREE.Mesh(outlineSphereGeometry, new THREE.MeshBasicMaterial({ 
            color: axisColor
        }));
        outlineSphere.raycast = () => {};
        
        this.spheres.set(positiveSphere, this.getPositiveDirection(axisName));
        this.spheres.set(negativeSphere, this.getNegativeDirection(axisName));
        
        this.addLabel(positiveSphere, axisName, true);
        this.addLabel(negativeSphere, axisName, false);
        
        this.circleOutlines.push({ outlineSphere, targetSphere: negativeSphere });
        
        this.sphereGroup.add(positiveSphere);
        this.sphereGroup.add(negativeSphere);
        this.scene.add(outlineSphere);
    }
    
    private addLabel(sphere: THREE.Mesh, axisName: string, isPositive: boolean): void {
        const text = (isPositive ? '+' : '-') + axisName.toUpperCase();
        
        let blackTexture: THREE.CanvasTexture | undefined;
        
        if (isPositive) {
            const blackCanvas = document.createElement('canvas');
            const blackContext = blackCanvas.getContext('2d')!;
            blackCanvas.width = 256;
            blackCanvas.height = 256;
            blackContext.font = 'bold 150px Consolas';
            blackContext.fillStyle = '#010101';
            blackContext.textAlign = 'center';
            blackContext.textBaseline = 'middle';
            blackContext.fillText(text, blackCanvas.width / 2, blackCanvas.height / 2);
            blackTexture = new THREE.CanvasTexture(blackCanvas);
        }
        
        const whiteCanvas = document.createElement('canvas');
        const whiteContext = whiteCanvas.getContext('2d')!;
        whiteCanvas.width = 256;
        whiteCanvas.height = 256;
        whiteContext.font = 'bold 150px Consolas';
        whiteContext.fillStyle = '#ffffff';
        whiteContext.textAlign = 'center';
        whiteContext.textBaseline = 'middle';
        whiteContext.fillText(text, whiteCanvas.width / 2, whiteCanvas.height / 2);
        const whiteTexture = new THREE.CanvasTexture(whiteCanvas);
        
        const material = new THREE.SpriteMaterial({
            map: isPositive ? blackTexture! : whiteTexture,
            transparent: true,
            alphaTest: 0.1,
            opacity: isPositive ? 1 : 0
        });
        
        const sprite = new THREE.Sprite(material);
        sprite.scale.set(0.3, 0.3, 1);
        sprite.raycast = () => {};
        
        this.labels.push({ 
            sprite, 
            sphere, 
            isPositive,
            blackTexture,
            whiteTexture
        });
        
        this.scene.add(sprite);
    }
    
    private getPositiveDirection(axis: string): ViewDirection {
        switch (axis) {
            case 'x': return ViewDirection.POSITIVE_X;
            case 'y': return ViewDirection.POSITIVE_Y;
            case 'z': return ViewDirection.POSITIVE_Z;
            default: return ViewDirection.POSITIVE_X;
        }
    }
    
    private getNegativeDirection(axis: string): ViewDirection {
        switch (axis) {
            case 'x': return ViewDirection.NEGATIVE_X;
            case 'y': return ViewDirection.NEGATIVE_Y;
            case 'z': return ViewDirection.NEGATIVE_Z;
            default: return ViewDirection.NEGATIVE_X;
        }
    }
    
    public syncWithCamera(quaternion: THREE.Quaternion): void {
        this.gizmoGroup.quaternion.copy(quaternion).invert();
    }
    
    public async render(renderer: RendererType): Promise<void> {
        this.labels.forEach(({ sprite, sphere, isPositive, blackTexture, whiteTexture }) => {
            const sphereWorldPos = sphere.getWorldPosition(new THREE.Vector3());
            const direction = sphereWorldPos.clone().sub(this.camera.position).normalize();
            const offset = this.sphereRadius + this.labelOffset;
            sprite.position.copy(sphereWorldPos).add(direction.multiplyScalar(-offset));
            sprite.lookAt(this.camera.position);
            
            const material = sprite.material as THREE.SpriteMaterial;
            const isHovered = this.hoveredSphere === sphere;
            
            if (isPositive) {
                material.map = isHovered ? whiteTexture : (blackTexture!);
                material.opacity = 1;
            } else {
                material.map = whiteTexture;
                material.opacity = isHovered ? 1 : 0;
            }
        });
        
        this.circleOutlines.forEach(({ outlineSphere, targetSphere }) => {
            const sphereWorldPos = targetSphere.getWorldPosition(new THREE.Vector3());
            const direction = sphereWorldPos.clone().sub(this.camera.position).normalize();
            const offset = this.sphereRadius * 0.5;
            outlineSphere.position.copy(sphereWorldPos).add(direction.multiplyScalar(offset));
        });

        await renderer.render(this.scene, this.camera);
    }
    
    private getSphereAtPosition(mouseX: number, mouseY: number, viewportSize: number): THREE.Mesh | null {
        this.gizmoGroup.updateMatrixWorld(true);
        
        const x = (mouseX / viewportSize) * 2 - 1;
        const y = -(mouseY / viewportSize) * 2 + 1;
        
        this.raycaster.setFromCamera(new THREE.Vector2(x, y), this.camera);
        const intersects = this.raycaster.intersectObjects([this.gizmoGroup], true);
        
        if (intersects.length > 0) {
            for (const intersect of intersects) {
                const hitObject = intersect.object as THREE.Mesh;
                if (this.spheres.has(hitObject)) {
                    return hitObject;
                }
            }
        }
        
        return null;
    }
    
    public handleHover(mouseX: number, mouseY: number, viewportSize: number): void {
        if (mouseX < 0 || mouseY < 0) {
            this.hoveredSphere = null;
            return;
        }
        this.hoveredSphere = this.getSphereAtPosition(mouseX, mouseY, viewportSize);
    }
    
    public handleClick(mouseX: number, mouseY: number, viewportSize: number): ViewDirection | null {
        const hitSphere = this.getSphereAtPosition(mouseX, mouseY, viewportSize);
        if (hitSphere) {
            return this.spheres.get(hitSphere) || null;
        }
        return null;
    }
    
    public static getViewParameters(direction: ViewDirection, _distance: number): { azimuth: number; polar: number } {
        switch (direction) {
            case ViewDirection.POSITIVE_X:
                return { azimuth: Math.PI / 2, polar: Math.PI / 2 };
            case ViewDirection.NEGATIVE_X:
                return { azimuth: -Math.PI / 2, polar: Math.PI / 2 };
            case ViewDirection.POSITIVE_Y:
                return { azimuth: 0, polar: 0 };
            case ViewDirection.NEGATIVE_Y:
                return { azimuth: 0, polar: Math.PI };
            case ViewDirection.POSITIVE_Z:
                return { azimuth: 0, polar: Math.PI / 2 };
            case ViewDirection.NEGATIVE_Z:
                return { azimuth: Math.PI, polar: Math.PI / 2 };
            default:
                return { azimuth: 0, polar: Math.PI / 2 };
        }
    }
    
    public dispose(): void {
        this.spheres.forEach((_, sphere) => {
            if (sphere.geometry) {
                sphere.geometry.dispose();
            }
            if (sphere.material) {
                (sphere.material as THREE.MeshBasicMaterial).dispose();
            }
        });
        
        this.labels.forEach(({ sprite, blackTexture, whiteTexture }) => {
            if (sprite.material) {
                const material = sprite.material as THREE.SpriteMaterial;
                material.dispose();
            }
            if (blackTexture) {
                blackTexture.dispose();
            }
            if (whiteTexture) {
                whiteTexture.dispose();
            }
        });
        
        this.circleOutlines.forEach(({ outlineSphere }) => {
            if (outlineSphere.geometry) {
                outlineSphere.geometry.dispose();
            }
            if (outlineSphere.material) {
                (outlineSphere.material as THREE.MeshBasicMaterial).dispose();
            }
        });
        
        this.scene.clear();
    }
}

/**
 * ViewHelperTools3D - 3D 版本的 ViewHelper，使用 Three.js 3D 对象渲染
 * 基于 PaleEngineWeb 的 ViewHelperGizmo 实现
 */
export class ViewHelperTools3D extends BaseUITools {
    private visionaryScene: VisionaryScene;
    private viewHelperGizmo!: ViewHelperGizmo;
    private renderer!: RendererType;
    private animationId: number = 0;
    private viewHelperContainer: HTMLElement;
    private viewHelperController: ViewHelperController;
    private cameraController: any; // CameraController 实例（可选）
    private boundMouseMove: (event: MouseEvent) => void;
    private boundMouseClick: (event: MouseEvent) => void;
    private boundResize: () => void;
    
    constructor(containerId: string, visionaryScene: VisionaryScene, cameraController?: any) {
        super(containerId, false);
        this.visionaryScene = visionaryScene;
        this.viewHelperContainer = this.container;
        this.viewHelperController = new ViewHelperController(visionaryScene);
        this.cameraController = cameraController;
        
        // 绑定事件处理函数
        this.boundMouseMove = this.handleMouseMove.bind(this);
        this.boundMouseClick = this.handleMouseClick.bind(this);
        this.boundResize = this.handleResize.bind(this);
        
        // 异步初始化，不阻塞构造函数
        this.init().catch(error => {
            console.error('ViewHelperTools3D 初始化失败:', error);
        });
    }
    
    async init(): Promise<void> {
        await this.initRenderer();
        this.initViewHelperGizmo();
        this.setupEventListeners();
        this.startRenderLoop();
    }
    
    private async initRenderer(): Promise<void> {
        const container = this.viewHelperContainer;
        if (!container) {
            throw new Error(`ViewHelper container element not found`);
        }
        
        // 创建 Canvas 元素
        const canvas = document.createElement('canvas');
        canvas.width = container.clientWidth || 200;
        canvas.height = container.clientHeight || 200;
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        canvas.style.display = 'block';
        container.appendChild(canvas);
        
        // 创建 WebGL 渲染器（ViewHelper 不需要 WebGPU，WebGL 足够）
        // 设置 alpha: true 使背景透明
        this.renderer = new THREE.WebGLRenderer({ 
            antialias: true, 
            canvas,
            alpha: true  // 启用透明背景
        });
        this.renderer.setClearColor(0x000000, 0); // 设置透明背景
        this.renderer.setSize(container.clientWidth || 200, container.clientHeight || 200);
        this.renderer.setPixelRatio(window.devicePixelRatio);
    }
    
    private initViewHelperGizmo(): void {
        // 根据容器大小动态计算 gizmo 尺寸，使其适合容器
        const container = this.viewHelperContainer;
        const viewportSize = Math.min(container.clientWidth || 200, container.clientHeight || 200);

        const axisLength = Math.max(0.5, (viewportSize / 200) * 0.5);
        
        this.viewHelperGizmo = new ViewHelperGizmo({
            size: axisLength
        });
    }
    
    private setupEventListeners(): void {
        this.viewHelperContainer.addEventListener('mousemove', this.boundMouseMove);
        this.viewHelperContainer.addEventListener('click', this.boundMouseClick);
        window.addEventListener('resize', this.boundResize);
    }
    
    private handleResize(): void {
        const container = this.viewHelperContainer;
        if (container && this.renderer) {
            const width = container.clientWidth || 200;
            const height = container.clientHeight || 200;
            this.renderer.setSize(width, height);
            
            // 重新计算 gizmo 尺寸以适应新的容器大小
            if (this.viewHelperGizmo) {
                const viewportSize = Math.min(width, height);
                const axisLength = Math.max(0.3, (viewportSize / 200) * 0.3);
                // 注意：ViewHelperGizmo 目前不支持动态调整尺寸，需要重新创建
                // 这里先不处理，因为重建 gizmo 会影响用户体验
                // 可以考虑在 ViewHelperGizmo 中添加 updateSize 方法
            }
        }
    }
    
    private handleMouseMove(event: MouseEvent): void {
        const rect = this.viewHelperContainer.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;
        const viewportSize = Math.min(rect.width, rect.height);
        
        this.viewHelperGizmo.handleHover(mouseX, mouseY, viewportSize);
    }
    
    private handleMouseClick(event: MouseEvent): void {
        const rect = this.viewHelperContainer.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;
        const viewportSize = Math.min(rect.width, rect.height);
        
        const direction = this.viewHelperGizmo.handleClick(mouseX, mouseY, viewportSize);
        if (direction) {
            // 转换 ViewDirection 到 ViewHelperController 的格式
            const axisMap: Record<ViewDirection, string> = {
                [ViewDirection.POSITIVE_X]: 'px',
                [ViewDirection.NEGATIVE_X]: 'nx',
                [ViewDirection.POSITIVE_Y]: 'py',
                [ViewDirection.NEGATIVE_Y]: 'ny',
                [ViewDirection.POSITIVE_Z]: 'pz',
                [ViewDirection.NEGATIVE_Z]: 'nz'
            };
            
            const axis = axisMap[direction];
            if (axis) {
                // 执行视角跳转
                this.viewHelperController.alignCamera(axis);
                
                // 同步 CameraController 的内部状态，防止拖动时跳回原视角
                // if (this.cameraController && typeof this.cameraController.syncFromCamera === 'function') {
                //     this.cameraController.syncFromCamera();
                // }
                if (this.cameraController && typeof this.cameraController.syncFromGizmoHelper === 'function') {
                    this.cameraController.syncFromGizmoHelper();
                }
            }
        }
    }
    
    private startRenderLoop(): void {
        const render = () => {
            this.animationId = requestAnimationFrame(render);
            this.renderFrame();
        };
        render();
    }
    
    private async renderFrame(): Promise<void> {
        if (!this.renderer || !this.viewHelperGizmo) return;
        
        const camera = this.visionaryScene.getCamera();
        if (!camera) return;
        
        // 同步相机四元数到 ViewHelper
        this.viewHelperGizmo.syncWithCamera(camera.quaternion);
        
        // 渲染 ViewHelper
        await this.viewHelperGizmo.render(this.renderer);
    }
    
    public dispose(): void {
        // 停止渲染循环
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = 0;
        }
        
        // 移除事件监听器
        if (this.viewHelperContainer) {
            this.viewHelperContainer.removeEventListener('mousemove', this.boundMouseMove);
            this.viewHelperContainer.removeEventListener('click', this.boundMouseClick);
            window.removeEventListener('resize', this.boundResize);
        }
        
        // 清理 ViewHelperGizmo
        if (this.viewHelperGizmo) {
            this.viewHelperGizmo.dispose();
        }
        
        // 清理渲染器
        if (this.renderer) {
            const canvas = this.renderer.domElement;
            if (canvas && canvas.parentNode) {
                canvas.parentNode.removeChild(canvas);
            }
            if (typeof (this.renderer as any).dispose === 'function') {
                (this.renderer as any).dispose();
            }
            this.renderer = null!;
        }
        
        // 调用基类销毁方法
        super.destroy();
    }
}
